<template>
  <div class="login-container">
    <div class="login-shell">
      <section class="login-hero" aria-label="Payroll system welcome">
        <div class="login-hero__content">
          <div class="login-seal">
            <img src="../../assets/vsu_logo_official.png" alt="Visayas State University seal">
          </div>
          <h1>VSU Payroll</h1>
          <p class="login-hero__subtitle">Payroll Management System</p>
          <span class="login-hero__accent" aria-hidden="true"/>
          <p class="login-hero__description">
            A secure, unified platform for payroll processing, personnel remittances, and government contribution reporting.
          </p>
        </div>
      </section>

      <section class="login-panel" aria-label="Sign in form">
        <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          auto-complete="on"
          label-position="left"
          @submit.native.prevent="handleLogin">
          <div class="login-copy">
            <h2>Welcome Back</h2>
            <p>Please enter your credentials to access your account</p>
          </div>

          <el-form-item prop="email" class="login-field">
            <span class="svg-container">
              <svg-icon icon-class="user" />
            </span>
            <el-input
              v-model.trim="loginForm.email"
              name="email"
              type="email"
              auto-complete="username"
              placeholder="Email address" />
          </el-form-item>

          <el-form-item prop="password" class="login-field">
            <span class="svg-container">
              <svg-icon icon-class="key" />
            </span>
            <el-input
              :type="pwdType"
              v-model="loginForm.password"
              name="password"
              auto-complete="current-password"
              placeholder="Password"
              @keyup.enter.native="handleLogin" />
            <button type="button" class="show-pwd" aria-label="Toggle password visibility" @click="showPwd">
              <svg-icon icon-class="eye" />
            </button>
          </el-form-item>

          <div class="login-actions">
            <router-link :to="'forgot_password'" class="forgot-link">Forgot password?</router-link>
          </div>

          <el-button :loading="loading" type="primary" class="login-submit" @click.native.prevent="handleLogin">
            Sign in
          </el-button>

          <el-divider class="login-divider">or</el-divider>

          <el-button :disabled="!hasGoogleAuth" class="google-button" type="plain" @click="socialOAuth('google')">
            <i class="fab fa-google text-google"/>
            <span>Sign in with Google</span>
          </el-button>
        </el-form>
      </section>
    </div>
  </div>
</template>

<script>

import { LoginSocialAuthLinks } from '../../api/login'

export default {
  name: 'Login',
  data() {
    const validateUsername = (rule, value, callback) => {
      if (!value) {
        callback(new Error('Email is required'))
      } else {
        callback()
      }
    }
    const validatePass = (rule, value, callback) => {
      if (!value) {
        callback(new Error('Password is required'))
        return
      }
      if (value.length < 5) {
        callback(new Error('Password cannot be less than 5 characters'))
      } else {
        callback()
      }
    }
    return {
      loginForm: {
        email: '',
        password: ''
      },
      loginRules: {
        email: [{ required: true, trigger: 'blur', validator: validateUsername }],
        password: [{ required: true, trigger: 'blur', validator: validatePass }]
      },
      loading: false,
      pwdType: 'password',
      redirect: undefined,
      socialAuthLinks: null
    }
  },
  computed: {
    hasGoogleAuth() {
      return Boolean(this.socialAuthLinks && this.socialAuthLinks.google)
    }
  },
  watch: {
    $route: {
      handler: function(route) {
        this.redirect = route.query && route.query.redirect
      },
      immediate: true
    }
  },
  mounted() {
    this.getSocialLinks()
  },
  methods: {
    showPwd() {
      if (this.pwdType === 'password') {
        this.pwdType = 'text'
      } else {
        this.pwdType = 'password'
      }
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true
          this.$store.dispatch('Login', this.loginForm).then(() => {
            this.loading = false
            this.$router.push({ path: this.redirect || '/' })
          }).catch(() => {
            this.loading = false
          })
        } else {
          console.log('error submit!!')
          return false
        }
      })
    },
    getSocialLinks() {
      LoginSocialAuthLinks({ link: 'payroll' }).then(res => {
        this.socialAuthLinks = res.data
      })
    },
    socialOAuth(provider) {
      if (!this.socialAuthLinks || !this.socialAuthLinks[provider]) {
        return
      }
      window.location = this.socialAuthLinks[provider]
    }
  }
}
</script>

<style rel="stylesheet/scss" lang="scss">
  $input_bg:#121a2d;

  /* reset element-ui css */
  .login-container {
    .el-input {
      display: block;
      height: 48px;
      width: 100%;
      input {
        background: transparent;
        border: 0;
        -webkit-appearance: none;
        border-radius: 0;
        padding: 12px 44px 12px 46px;
        color: #edf3f0;
        height: 48px;
        font-size: 14px;
        &:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 1000px $input_bg inset !important;
          -webkit-text-fill-color: #edf3f0 !important;
        }
      }
    }
    .el-form-item {
      margin-bottom: 20px;
      color: #edf3f0;
    }
    .el-form-item__content {
      line-height: 48px;
    }
    .login-field {
      .el-form-item__content {
        background: $input_bg;
        border: 1px solid #263049;
        border-radius: 8px;
        transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
      }
      .el-form-item__content:focus-within {
        background: #151f35;
        border-color: #008755;
        box-shadow: 0 0 0 3px rgba(0, 135, 85, .16);
      }
    }
    .el-form-item.is-error .el-form-item__content {
      border-color: #f56c6c;
    }
    .el-form-item__error {
      padding-top: 5px;
      color: #ff8a8a;
    }
    .el-divider__text {
      background: #0b1226;
      color: #76839a;
      font-size: 12px;
    }
  }

  .login-container .el-input input::placeholder { color: #7d899f; }
  .text-google { color: #f4b400; }

  .login-divider { margin: 28px 0; }
  .login-divider.el-divider { background-color: #222b43; }
  .text-center { text-align: center; }

</style>

<style rel="stylesheet/scss" lang="scss" scoped>
  $brand_green:#00573f;
  $brand_green_dark:#003c31;
  $brand_green_light:#008755;
  $brand_gold:#fdb913;
  $navy:#050b1d;
  $panel:#0b1226;
  $panel_border:#202a43;
  $text:#f4f7f5;
  $muted:#8b98ad;

  .login-container {
    min-height: 100vh;
    width: 100%;
    overflow-y: auto;
    background:
      linear-gradient(90deg, $brand_green_dark 0%, $brand_green 46%, $navy 46%, $navy 100%);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Roboto', Arial, sans-serif;

    .login-shell {
      width: 100%;
      min-height: 100vh;
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(480px, 1fr);
    }

    .login-hero {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      color: $text;
      background:
        radial-gradient(circle at 28% 38%, rgba(0, 135, 85, .34), transparent 31%),
        linear-gradient(135deg, $brand_green 0%, $brand_green_dark 100%);
      padding: 64px;
    }

    .login-hero:before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        linear-gradient(90deg, rgba(0, 0, 0, .04), rgba(5, 11, 29, .08)),
        radial-gradient(circle at 72% 62%, rgba(253, 185, 19, .09), transparent 24%);
    }

    .login-hero__content {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: 470px;
    }

    .login-seal {
      width: 100px;
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 38px;
      border: 1px solid rgba(255, 255, 255, .18);
      border-radius: 8px;
      background: rgba(255, 255, 255, .08);
      box-shadow: 0 20px 40px rgba(0, 0, 0, .16);

      img {
        width: 72px;
        height: 72px;
        object-fit: contain;
        display: block;
      }
    }

    .login-hero h1 {
      margin: 0 0 10px;
      color: $text;
      font-family: 'Montserrat', 'Roboto', Arial, sans-serif;
      font-size: 46px;
      line-height: 1.05;
      font-weight: 700;
      letter-spacing: 0;
    }

    .login-hero__subtitle {
      margin: 0;
      color: $brand_gold;
      font-family: 'Montserrat', 'Roboto', Arial, sans-serif;
      font-size: 22px;
      font-weight: 600;
      line-height: 1.35;
    }

    .login-hero__accent {
      width: 78px;
      height: 3px;
      display: block;
      margin: 22px 0 28px;
      background: linear-gradient(90deg, $brand_gold, rgba(253, 185, 19, 0));
    }

    .login-hero__description {
      margin: 0;
      max-width: 480px;
      color: #aab7b8;
      font-size: 17px;
      line-height: 1.55;
    }

    .login-panel {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 64px;
      background: $navy;
    }

    .login-form {
      width: 100%;
      max-width: 440px;
      padding: 50px 48px;
      margin: 0 auto !important;
      border: 1px solid $panel_border;
      border-radius: 8px;
      background: rgba(11, 18, 38, .82);
      box-shadow: 0 24px 60px rgba(0, 0, 0, .2);
    }

    .login-copy {
      margin-bottom: 46px;
    }

    .login-copy h2 {
      margin: 0 0 8px;
      color: $text;
      font-family: 'Montserrat', 'Roboto', Arial, sans-serif;
      font-size: 29px;
      line-height: 1.12;
      font-weight: 700;
      letter-spacing: 0;
    }

    .login-copy p {
      margin: 0;
      color: $muted;
      font-size: 15px;
      line-height: 1.25;
    }

    .tips {
      font-size: 14px;
      color: #fff;
      margin-bottom: 10px;
      span {
        &:first-of-type {
          margin-right: 16px;
        }
      }
    }
    .svg-container {
      position: absolute;
      left: 16px;
      top: 0;
      color: #7f8ca3;
      width: 30px;
      height: 48px;
      display: flex;
      align-items: center;
      z-index: 1;
    }
    .title {
      font-size: 26px;
      font-weight: 400;
      color: #ffffff;
      margin: 0 auto 40px auto;
      text-align: center;
      font-weight: bold;
    }
    .login-actions {
      display: flex;
      justify-content: flex-end;
      margin: -6px 0 20px;
    }

    .forgot-link {
      color: #00c58f;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
    }

    .forgot-link:hover {
      color: $brand_gold;
      text-decoration: underline;
    }

    .show-pwd {
      position: absolute;
      right: 10px;
      top: 6px;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 0;
      border-radius: 8px;
      background: transparent;
      font-size: 16px;
      color: #7f8ca3;
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .2s ease, color .2s ease;
      z-index: 2;
    }

    .show-pwd:hover {
      background: rgba(0, 135, 85, .1);
      color: #00c58f;
    }

    .login-submit {
      width: 100%;
      min-height: 48px;
      border: 0;
      border-radius: 8px;
      background: #006b4b;
      color: #ffffff;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0;
      box-shadow: 0 14px 26px rgba(0, 87, 63, .24);
    }

    .login-submit:hover,
    .login-submit:focus {
      background: $brand_green_light;
      box-shadow: 0 16px 30px rgba(0, 135, 85, .26);
    }

    .login-divider {
      display: none;
    }

    .google-button {
      width: 100%;
      min-height: 42px;
      margin-top: 16px;
      border-radius: 8px;
      border-color: $panel_border;
      color: #c5cfdd;
      font-weight: 600;
      background: transparent;

      span {
        margin-left: 10px;
      }
    }

    .google-button:hover,
    .google-button:focus {
      border-color: rgba(253, 185, 19, .48);
      color: $brand_gold;
      background: rgba(253, 185, 19, .04);
    }
  }

  @media (max-width: 900px) {
    .login-container {
      align-items: flex-start;
      background: $navy;
      padding: 0;

      .login-shell {
        min-height: auto;
        grid-template-columns: 1fr;
      }

      .login-hero {
        min-height: 330px;
        padding: 34px;
        background:
          radial-gradient(circle at 82% 12%, rgba(253, 185, 19, .12), transparent 28%),
          linear-gradient(135deg, $brand_green 0%, $brand_green_dark 100%);
      }

      .login-hero h1 {
        font-size: 34px;
      }

      .login-hero__subtitle {
        font-size: 18px;
      }

      .login-seal {
        width: 84px;
        height: 84px;
        margin-bottom: 26px;

        img {
          width: 60px;
          height: 60px;
        }
      }

      .login-panel {
        min-height: auto;
        padding: 34px 24px;
      }
    }
  }

  @media (max-width: 520px) {
    .login-container {
      padding: 0;

      .login-shell {
        display: block;
      }

      .login-hero {
        min-height: 300px;
        padding: 26px;
      }

      .login-hero__description {
        font-size: 14px;
      }

      .login-panel {
        padding: 24px 16px 32px;
      }

      .login-form {
        padding: 32px 22px;
      }

      .login-copy h2 {
        font-size: 24px;
      }
    }
  }
</style>
